// useVideoAccess.js — the single place that decides whether a video may be played.
//
// THE WALL LIVES SERVER-SIDE, at /api/media/video/{id}/meta, which withholds `storage_path`
// from signed-out callers. This hook is how the UI asks. It exists because six surfaces used to
// build a video URL directly from an id and play it, which walked straight past the wall — the
// streaming endpoint has to stay open, since a <video> tag cannot send an auth header.
//
// Anything that plays a video should use this. A new surface then inherits the wall instead of
// having to remember it.
import { useEffect, useState } from "react";
import { apiService } from "../apiService";
import { useAuth } from "../AuthContext";
import { storageUrl } from "../utils/thumbnailPreloader";

/**
 * @param {string} videoId
 * @param {object} [seed] - values already known (e.g. from navigation state), so a surface that
 *   already has a playable URL does not flash a wall while re-checking.
 * @returns {{videoUrl, subtitleUrl, title, requiresSignIn, loading, error}}
 */
export function useVideoAccess(videoId, seed = null) {
  const { user } = useAuth();
  const [state, setState] = useState({
    videoUrl: seed?.videoUrl || null,
    subtitleUrl: seed?.subtitleUrl || null,
    title: seed?.title || null,
    requiresSignIn: false,
    loading: !seed?.videoUrl,
    error: null,
  });

  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;

    setState((prev) => ({ ...prev, loading: !prev.videoUrl, error: null }));

    apiService
      .makeRequest(`/api/media/video/${videoId}/meta`)
      .then((meta) => {
        if (cancelled) return;

        if (meta.requires_signin) {
          // No path came back, so there is nothing to play. Keep the title — someone arriving
          // from a shared link should see what they are being asked to sign in for.
          setState({
            videoUrl: null,
            subtitleUrl: null,
            title: meta.title || seed?.title || null,
            requiresSignIn: true,
            loading: false,
            error: null,
          });
          return;
        }

        // signed_url is no longer sent — reels went back to public objects with permanent
        // URLs (2026-09-13) — but it still wins when present, so an older cached response or a
        // future re-restriction keeps playing without a client change.
        const videoUrl =
          meta.signed_url ||
          (meta.storage_path ? storageUrl(meta.storage_path) : apiService.getVideoUrl(videoId));
        const subtitleUrl = meta.subtitle_storage_path
          ? storageUrl(meta.subtitle_storage_path)
          : apiService.getSubtitleUrl(videoId);

        setState({
          videoUrl,
          subtitleUrl,
          title: meta.title || seed?.title || null,
          requiresSignIn: false,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 401) {
          setState((s) => ({ ...s, requiresSignIn: true, videoUrl: null, loading: false }));
        } else if (err.status === 403) {
          setState((s) => ({ ...s, videoUrl: null, loading: false, error: "restricted" }));
        } else {
          // Deliberately NOT falling back to the streaming URL: that endpoint is open, so
          // doing so would route around the wall we just hit.
          setState((s) => ({ ...s, loading: false, error: "unavailable" }));
        }
      });

    return () => { cancelled = true; };
    // Re-runs on sign-in so the video starts by itself — no "now press play again" step.
  }, [videoId, user?.uid, user?.isAnonymous]);

  return state;
}
